import { deleteDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { periodNoteRef } from './paths'
import { recordTagUse } from './tags'
import type { NoteScope, PeriodNote } from './types'

/** Day notes key on the logical day; week notes key on that week's Monday. */
export function periodNoteId(scope: NoteScope, key: string): string {
  return `${scope}-${key}`
}

export interface PeriodNoteValue {
  note: string | null
  tags: string[]
}

export const EMPTY_NOTE: PeriodNoteValue = { note: null, tags: [] }

export function subscribePeriodNote(
  uid: string,
  scope: NoteScope,
  key: string,
  onChange: (value: PeriodNoteValue) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    periodNoteRef(uid, periodNoteId(scope, key)),
    (snapshot) => {
      const data = snapshot.data()
      onChange(data ? { note: data.note ?? null, tags: data.tags ?? [] } : EMPTY_NOTE)
    },
    (error) => onError?.(error),
  )
}

export async function writePeriodNote(
  uid: string,
  scope: NoteScope,
  key: string,
  value: PeriodNoteValue,
): Promise<void> {
  const ref = periodNoteRef(uid, periodNoteId(scope, key))
  // An emptied note is deleted rather than left as a blank document, so the
  // "has a note" indicator stays honest.
  if (!value.note && value.tags.length === 0) {
    await deleteDoc(ref).catch(() => undefined)
    return
  }
  await setDoc(ref, {
    scope,
    key,
    note: value.note,
    tags: value.tags,
    updatedAt: serverTimestamp(),
  } as unknown as PeriodNote)
  await recordTagUse(uid, value.tags)
}
